/**
 * Page Performance → Companies (only in the all-companies view): each
 * company's traffic and speed from its own page opens, server-paged,
 * sortable and searchable by name. A row narrows every tab to that company.
 */
import { useMemo } from 'react'
import { Building2 } from 'lucide-react'
import { DataTable } from '../table/DataTable'
import { extractApiError } from '../../lib/api'
import { fetchAllOrganisations, useOrganisations } from '../../lib/page-perf/api-client'
import { normaliseLimits, periodPhrase } from '../../lib/page-perf/view-model'
import { buildCompanyColumns } from './perf-columns'
import { FilterSpinner } from './PerfUi'
import { usePerfTable } from './use-perf-table'

export function PerfCompaniesTab({ ctx }) {
  const { scope, go, slot } = ctx
  const table = usePerfTable({
    defaultSort: [{ key: 'p95Ms', dir: 'desc' }],
    resetKey: `${scope.period}|${scope.compare}`,
  })
  const query = useOrganisations(scope, table.params)
  const data = query.data
  const limits = useMemo(() => normaliseLimits(data?.limits), [data?.limits])
  const narrow = useMemo(() => (row) => go({ organization: row.tenantId, tab: 'overview' }), [go])
  const columns = useMemo(() => buildCompanyColumns({ slot, limits, onNarrow: narrow }), [slot, limits, narrow])
  const total = data?.total ?? 0

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-xs text-[color:var(--color-ink-muted)]">
          Each row comes only from that company&rsquo;s own page opens · a company&rsquo;s colour is its worst page · click a row
          to narrow every tab to it.
        </p>
        <FilterSpinner show={query.isFetching && query.isPlaceholderData} />
      </div>
      <DataTable
        tableId="page-perf-companies"
        columns={columns}
        rows={data?.rows ?? []}
        getRowId={(r) => r.tenantId}
        getRowProps={(r) => ({ onClick: () => narrow(r), className: 'cursor-pointer' })}
        loading={query.isLoading}
        fetching={query.isFetching && !query.isLoading}
        error={query.isError && !data ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        search={table.q}
        onSearchChange={table.setQ}
        searchPlaceholder="Search by company name"
        defaultSort={table.sort}
        onSortChange={table.setSort}
        page={table.page}
        totalPages={Math.max(1, Math.ceil(total / Math.max(1, table.limit)))}
        total={total}
        limit={table.limit}
        limitOptions={table.limitOptions}
        onPageChange={table.setPage}
        onLimitChange={table.setLimit}
        exportBaseName="page-performance-companies"
        exportTitle="Page Performance — Companies"
        getExportRows={() => fetchAllOrganisations(scope, table.params)}
        emptyState={{
          icon: Building2,
          title: table.q.trim()
            ? `No company matches “${table.q.trim()}” ${periodPhrase(scope.period)}`
            : `No company had page opens ${periodPhrase(scope.period)}`,
          body: 'Companies appear here once their members open pages.',
        }}
      />
    </div>
  )
}
